import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import * as Toast from "@radix-ui/react-toast";
import { CheckCircle2, XCircle } from "lucide-react";

/*
 * Toast no canto inferior direito (doc 06a §5).
 *
 * O texto segue a regra da §6: **o verbo do botão é o verbo do resultado**.
 * Botão "Cancelar boleto" → aviso "Boleto cancelado". Um "Sucesso!" genérico
 * não diz o que aconteceu, e o operador que clicou em três ações seguidas não
 * consegue saber qual delas respondeu.
 */
type Tom = "feito" | "falhou";

interface Aviso {
  id: number;
  tom: Tom;
  texto: string;
  /** Detalhe opcional — o `aviso` que a API devolve nas ações assíncronas. */
  detalhe?: string;
}

interface Contexto {
  /** Anuncia que algo deu certo. Diga o fato, no passado: "Boleto cancelado". */
  feito: (texto: string, detalhe?: string) => void;
  falhou: (texto: string) => void;
}

const ContextoDeAvisos = createContext<Contexto | null>(null);

export function useAvisos(): Contexto {
  const contexto = useContext(ContextoDeAvisos);
  if (contexto === null) {
    throw new Error("useAvisos precisa de um <ProvedorDeAvisos> acima na árvore.");
  }
  return contexto;
}

export function ProvedorDeAvisos({ children }: { children: ReactNode }) {
  const [avisos, setAvisos] = useState<Aviso[]>([]);

  const empilhar = useCallback((tom: Tom, texto: string, detalhe?: string) => {
    setAvisos((atuais) => [
      ...atuais,
      { id: Date.now() + Math.random(), tom, texto, ...(detalhe ? { detalhe } : {}) },
    ]);
  }, []);

  const valor = useMemo<Contexto>(
    () => ({
      feito: (texto, detalhe) => empilhar("feito", texto, detalhe),
      falhou: (texto) => empilhar("falhou", texto),
    }),
    [empilhar],
  );

  return (
    <ContextoDeAvisos value={valor}>
      {/*
        `duration` maior para falha: uma mensagem de erro que some em três
        segundos é uma mensagem que o operador não leu. O sucesso pode sumir
        rápido — o efeito dele já está na tela.
      */}
      <Toast.Provider swipeDirection="right" label="Aviso">
        {children}

        {avisos.map((aviso) => (
          <Toast.Root
            key={aviso.id}
            duration={aviso.tom === "falhou" ? 8000 : 4000}
            onOpenChange={(aberto) => {
              if (!aberto) setAvisos((atuais) => atuais.filter((a) => a.id !== aviso.id));
            }}
            className="flex items-start gap-2 rounded-cartao border border-fio bg-superficie-2 px-3 py-2 shadow-none data-[state=open]:animate-[surgir_var(--entrada-toast)_ease-out]"
          >
            {aviso.tom === "feito" ? (
              <CheckCircle2 aria-hidden="true" className="mt-[2px] size-4 shrink-0 text-pago" />
            ) : (
              <XCircle aria-hidden="true" className="mt-[2px] size-4 shrink-0 text-falha" />
            )}

            <div className="min-w-0">
              <Toast.Title className="text-corpo text-texto">{aviso.texto}</Toast.Title>
              {aviso.detalhe !== undefined && (
                <Toast.Description className="mt-[2px] text-[12px] text-texto-2">
                  {aviso.detalhe}
                </Toast.Description>
              )}
            </div>
          </Toast.Root>
        ))}

        {/*
          Canto inferior direito (doc 06a §5), e acima do rodapé do painel lateral: colado
          na borda, o aviso de "salvo" cobria o botão Editar e engolia o clique seguinte.
        */}
        <Toast.Viewport className="fixed bottom-16 right-3 z-[80] flex w-[360px] max-w-[92vw] flex-col gap-2 outline-none" />
      </Toast.Provider>
    </ContextoDeAvisos>
  );
}
